import angular, { IHttpService, IQService, IPromise } from 'angular';
import { IFlowModelGenerationFileUploadResult } from '@Shared.Angular/@types/core/contracts/queryModel/flowModelGenerationFile/flowModelGenerationFileUploadResult';
import { Guid } from '@Shared.Angular/@types/guid';
import { HttpStatusCodes } from '@Shared.Angular/@types/core/contracts/constants/httpStatusCodes';
import { SharedAngular } from '@Angular.Runner/@types/sharedAngular';

export class FlowModelGenerationFileService {
  private readonly maxFileSizeBytes: number = 50 * 1024 * 1024;
  public static readonly maxVideoFileSizeBytes: number = 100 * 1024 * 1024;
  public static readonly maxAudioFileSizeBytes: number = 100 * 1024 * 1024;
  private readonly generationFileUploadTimeoutMilliseconds: number =
    10 * 60 * 1000;
  private readonly generationVideoFileExtensions = new Set([
    'mp4',
    'mov',
    'webm'
  ]);
  private readonly generationAudioFileExtensions = new Set([
    'mp3',
    'wav',
    'aac',
    'flac',
    'm4a'
  ]);

  static $inject = ['$http', '$q', 'APP_CONFIG', 'guidService'];

  constructor(
    private $http: IHttpService,
    private $q: IQService,
    private APP_CONFIG: SharedAngular.APP_CONFIG,
    private guidService: SharedAngular.GuidService
  ) {}

  createLocalFile(file: File): IUploadedGenerationFile {
    return {
      localId: this.guidService.new(),
      fileName: file.name,
      fileSize: file.size,
      isUploaded: false
    };
  }

  uploadFile(
    generationFile: File,
    localFileId: Guid
  ): IPromise<IUploadedGenerationFile> {
    const isVideoFile = this.isVideoFile(generationFile.name);
    const isAudioFile = this.isAudioFile(generationFile.name);
    const fileSizeLimitBytes = isVideoFile
      ? FlowModelGenerationFileService.maxVideoFileSizeBytes
      : isAudioFile
      ? FlowModelGenerationFileService.maxAudioFileSizeBytes
      : this.maxFileSizeBytes;

    if (generationFile.size > fileSizeLimitBytes) {
      return this.$q.reject({
        error: 'FILE_TOO_LARGE',
        message: `File exceeds ${Math.floor(
          fileSizeLimitBytes / (1024 * 1024)
        )}MB limit.`
      });
    }

    const formData = new FormData();
    formData.append('file', generationFile);

    const config: angular.IRequestShortcutConfig = {
      transformRequest: angular.identity,
      timeout: this.generationFileUploadTimeoutMilliseconds,
      headers: {
        'Content-Type': undefined as unknown as string
      },
      noSpinner: true
    };

    const uploadEndpoint = `${this.APP_CONFIG.apiBaseUrl}flowmodels/generationfiles`;

    return this.$http
      .post<IFlowModelGenerationFileUploadResult>(
        uploadEndpoint,
        formData,
        config
      )
      .then((response) => {
        const data = response.data;
        const uploadedFile: IUploadedGenerationFile = {
          id: data.flowModelGenerationFileId,
          isUploaded: true,
          localId: localFileId,
          fileName: data.fileName,
          fileSize: data.fileSizeBytes
        };
        return uploadedFile;
      })
      .catch((error) => {
        const errorResponse = error.data;
        // Server returns plain text error messages via global exception handler
        if (typeof errorResponse === 'string' && errorResponse.length > 0) {
          return this.$q.reject({
            error: 'SERVER_ERROR',
            message: errorResponse
          });
        }
        if (errorResponse?.error) {
          return this.$q.reject(errorResponse);
        }

        return this.$q.reject({
          error: 'SERVER_ERROR',
          message: 'Upload failed. Please check your connection and try again.'
        });
      });
  }

  deleteFile(generationFileId: Guid): IPromise<void> {
    return this.$http
      .delete<void>(
        `${this.APP_CONFIG.apiBaseUrl}flowmodels/generationfiles/${generationFileId}`
      )
      .then(() => undefined)
      .catch((error) => {
        if (error.status === HttpStatusCodes.NotFound) {
          return this.$q.resolve();
        }

        return this.$q.reject({
          error: 'SERVER_ERROR',
          message: 'Failed to delete file. Please try again.'
        });
      });
  }

  private isVideoFile(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    return this.generationVideoFileExtensions.has(extension);
  }

  private isAudioFile(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    return this.generationAudioFileExtensions.has(extension);
  }
}

angular
  .module('flowingly.services')
  .service('flowModelGenerationFileService', FlowModelGenerationFileService);

export interface IUploadedGenerationFile {
  id?: Guid;
  localId: Guid;
  fileName: string;
  fileSize: number;
  isUploaded: boolean;
}
